import { useNode } from "@craftjs/core";
import React from "react";
import { Box, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, SelectField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

export interface VideoProps {
  videoUrl: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  borderRadius?: number;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

export const Video = ({
  videoUrl,
  aspectRatio = "16:9",
  borderRadius = 8,
  autoplay = false,
  muted = true,
  loop = false,
}: VideoProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Convert YouTube/Vimeo URLs to embed URLs
  const getEmbedUrl = (url: string) => {
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      const params = new URLSearchParams();
      if (autoplay) params.set("autoplay", "1");
      if (muted) params.set("mute", "1");
      if (loop) params.set("loop", "1");
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?${params.toString()}`;
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      const params = new URLSearchParams();
      if (autoplay) params.set("autoplay", "1");
      if (muted) params.set("muted", "1");
      if (loop) params.set("loop", "1");
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?${params.toString()}`;
    }

    return url;
  };

  const aspectRatioMap = {
    "16:9": "56.25%",
    "4:3": "75%",
    "1:1": "100%",
  };

  const hasValidUrl = videoUrl && videoUrl.trim() !== "";

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        width: "100%",
        position: "relative",
        paddingTop: aspectRatioMap[aspectRatio],
        borderRadius: `${borderRadius}px`,
        overflow: "hidden",
        bgcolor: !hasValidUrl ? "#f3f4f6" : "transparent",
        // Create new stacking context to prevent iframe from overlapping elements below
        isolation: "isolate",
        zIndex: 0,
        ...(selected && selectedOutlineSx),
      }}
    >
      {hasValidUrl ? (
        <Box
          component="iframe"
          src={getEmbedUrl(videoUrl)}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#e5e7eb",
            border: "2px dashed #9ca3af",
            borderRadius: `${borderRadius}px`,
          }}
        >
          <PlayCircleOutlineIcon sx={{ fontSize: 48, color: "#6b7280", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Video
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add URL in settings
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const VideoSettings = () => {
  const {
    actions: { setProp },
    videoUrl,
    aspectRatio,
    borderRadius,
    autoplay,
    muted,
    loop,
  } = useNode((node) => ({
    videoUrl: node.data.props.videoUrl,
    aspectRatio: node.data.props.aspectRatio,
    borderRadius: node.data.props.borderRadius,
    autoplay: node.data.props.autoplay,
    muted: node.data.props.muted,
    loop: node.data.props.loop,
  }));

  return (
    <SettingsSection>
      <div>
        <TextField
          label="Video URL"
          value={videoUrl}
          onChange={(v) => setProp((props: VideoProps) => (props.videoUrl = v))}
          placeholder="YouTube or Vimeo URL"
        />
        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px" }}>Supports YouTube and Vimeo links</p>
      </div>

      <SelectField
        label="Aspect Ratio"
        value={aspectRatio}
        onChange={(v) => setProp((props: VideoProps) => (props.aspectRatio = v as VideoProps["aspectRatio"]))}
        options={[
          { value: "16:9", label: "16:9 (Widescreen)" },
          { value: "4:3", label: "4:3 (Standard)" },
          { value: "1:1", label: "1:1 (Square)" },
        ]}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: VideoProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <CheckboxField
        label="Autoplay"
        checked={autoplay}
        onChange={(v) => setProp((props: VideoProps) => (props.autoplay = v))}
      />

      <CheckboxField
        label="Muted"
        checked={muted}
        onChange={(v) => setProp((props: VideoProps) => (props.muted = v))}
      />

      <CheckboxField label="Loop" checked={loop} onChange={(v) => setProp((props: VideoProps) => (props.loop = v))} />
    </SettingsSection>
  );
};

Video.craft = {
  displayName: "Video",
  props: {
    videoUrl: "",
    aspectRatio: "16:9",
    borderRadius: 8,
    autoplay: false,
    muted: true,
    loop: false,
  },
  related: {
    settings: VideoSettings,
  },
};
